import { createLogger } from '../utils/logger';

const logger = createLogger('TokenManager');

type Tokens = { accessToken: string; refreshToken: string };

export class TokenManager {
  private refreshInFlight: Promise<void> | null = null;
  private lastRefreshAttempt: number = 0;
  private readonly MIN_REFRESH_INTERVAL = 60000; // Минимум 1 минута между попытками refresh (не спамить бэкенд)

  constructor(
    private loadTokens: () => Tokens | null,
    private saveTokens: (t: Tokens) => void,
    private clear: () => void,
    private refreshCall: (refreshToken: string) => Promise<Tokens>,
    private skewSeconds = 120 // буфер 2 мин: считаем access «истекающим» за 2 мин до exp
  ) {
    // Refresh только по требованию: при getAccessToken() перед API-запросом или при 401 (handle401AndRetry)
  }

  async getAccessToken(): Promise<string | null> {
    const t = this.loadTokens();
    if (!t?.accessToken) return null;
    if (!this.isExpiredSoon(t.accessToken)) return t.accessToken;

    try {
      await this.refreshIfNeeded(); // single-flight
      const newToken = this.loadTokens()?.accessToken ?? null;
      if (!newToken) {
        logger.warn('Failed to get new token after refresh');
      }
      return newToken;
    } catch (err) {
      logger.error('Failed to refresh token', err);
      return null;
    }
  }

  async getAuthHeader(): Promise<Record<string, string>> {
    const token = await this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async refreshIfNeeded(): Promise<void> {
    const t = this.loadTokens();
    if (!t?.refreshToken) throw new Error("no_refresh_token");

    if (t.accessToken && !this.isExpiredSoon(t.accessToken)) return;

    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRefreshAttempt;
    if (timeSinceLastAttempt < this.MIN_REFRESH_INTERVAL) return;

    if (!this.refreshInFlight) {
      this.lastRefreshAttempt = now;
      this.refreshInFlight = (async () => {
        const current = this.loadTokens();
        if (!current?.refreshToken) throw new Error("no_refresh_token");
        const refreshTokenUsed = current.refreshToken;
        (this as any)._refreshTokenUsed = refreshTokenUsed;

        const newTokens = await this.refreshCall(refreshTokenUsed);
        this.saveTokens(newTokens);
      })().catch((err) => {
        const isAuthError = err?.status === 401 ||
                           err?.status === 400 ||
                           err?.message?.includes('expired') ||
                           err?.message?.includes('invalid') ||
                           err?.message?.includes('Unauthorized') ||
                           err?.message?.includes('Refresh rejected') ||
                           err?.message?.includes('Refresh token missing') ||
                           err?.message?.includes('no_refresh_token');
        const isRateLimitError = err?.status === 429 || err?.message?.includes('Too Many Requests');
        const isNetworkError = err?.message?.includes('Failed to fetch') || err?.message?.includes('CORS');

        if (isAuthError) {
          // При быстром Cmd+R несколько загрузок могут одновременно дергать refresh;
          // бэкенд после первого успешного refresh инвалидирует старый токен — второй получает 401.
          // Не чистим токены, если в storage уже другой refresh token (другой запрос успел обновиться).
          const after = this.loadTokens();
          const used = (this as any)._refreshTokenUsed;
          if (after?.refreshToken && after.refreshToken !== used) {
            logger.warn('Refresh got 401 but storage has new token (concurrent refresh won), not clearing');
          } else {
            logger.error('Refresh failed with auth error, clearing tokens', err);
            this.clear();
            this.clearRefreshTimer();
          }
        } else if (isRateLimitError || isNetworkError) {
          logger.warn('Refresh failed (rate limit or network error), will retry later', err);
        } else {
          logger.warn('Refresh failed (other error)', err);
        }
        throw err;
      }).finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  async handle401AndRetry<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (e: any) {
      if (e?.status !== 401) throw e;
      await this.forceRefresh(); // через тот же single-flight
      return await request(); // retry только 1 раз
    }
  }

  async forceRefresh(): Promise<void> {
    const t = this.loadTokens();
    if (!t?.refreshToken) throw new Error("no_refresh_token");

    if (!this.refreshInFlight) {
      this.refreshInFlight = (async () => {
        const current = this.loadTokens();
        if (!current?.refreshToken) throw new Error("no_refresh_token");
        const refreshTokenUsed = current.refreshToken;
        (this as any)._refreshTokenUsed = refreshTokenUsed;
        const newTokens = await this.refreshCall(refreshTokenUsed);
        this.saveTokens(newTokens);
      })().catch((err) => {
        if (err?.status === 401 || err?.status === 400 || err?.message?.includes('expired') || err?.message?.includes('invalid') || err?.message?.includes('Refresh rejected') || err?.message?.includes('Refresh token missing') || err?.message?.includes('no_refresh_token')) {
          const after = this.loadTokens();
          const used = (this as any)._refreshTokenUsed;
          if (after?.refreshToken && after.refreshToken !== used) {
            logger.warn('Force refresh got 401 but storage has new token, not clearing');
          } else {
            this.clear();
            this.clearRefreshTimer();
          }
        }
        throw err;
      }).finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  clearTokens(): void {
    this.clear();
    this.clearRefreshTimer();
  }

  // Токены сохранены извне (например, после verifyToken). Refresh по-прежнему только по требованию.
  onTokensUpdated(): void {}

  private clearRefreshTimer(): void {
    // Таймер проактивного refresh отключён (refresh только по требованию)
  }

  private isExpiredSoon(jwt: string): boolean {
    try {
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      const expSec = payload?.exp;
      if (!expSec) return true;
      const nowSec = Math.floor(Date.now() / 1000);
      return expSec <= nowSec + this.skewSeconds;
    } catch {
      return true;
    }
  }

  // Получение времени истечения токена (для отладки)
  getTokenExpiration(): number | null {
    const tokens = this.loadTokens();
    if (!tokens?.accessToken) return null;

    try {
      const payload = JSON.parse(atob(tokens.accessToken.split(".")[1]));
      return payload?.exp ? payload.exp * 1000 : null; // Возвращаем в миллисекундах
    } catch {
      return null;
    }
  }
}
